import LinearProgress from '@mui/material/LinearProgress';
import dynamic from 'next/dynamic';
import { LayoutProps, LayoutSwitchProps } from '../../@types';
import DashboardLayout from './dashboard';
import { useAppContext, useAuth } from '../../hooks';
import { LoadingScreen, useSettings } from '@sdf/base';
import { DashboardNew } from './dashboard/DashboardNew';
import Box from '@mui/material/Box';
import { useTour } from '@reactour/tour';
import { useContext, useEffect } from 'react';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import {
  getTourStorageKey,
  getTourUserEmail,
  isTourCompletedForUser,
} from '../../utils/tourWelcome';

const MainLayout = dynamic(() => import('./main'), { loading: () => <LinearProgress /> });

const Page = dynamic(() => import('@sdf/base').then((lib) => lib.Page), {
  loading: () => <LinearProgress />,
});

function LayoutSwitch({
  variant,
  children,
  wrapperSx,
  containerSxProps,
  childrenWrapperSx,
  backBtnHandler,
}: LayoutSwitchProps) {
  if (variant === 'dashboard') {
    return <DashboardLayout wrapperSx={wrapperSx}>{children} </DashboardLayout>;
  }

  if (variant === 'dashboard-new') {
    return (
      <DashboardNew
        wrapperSx={wrapperSx}
        containerSxProps={containerSxProps}
        childrenWrapperSx={childrenWrapperSx}
        backBtnHandler={backBtnHandler}
      >
        {children}
      </DashboardNew>
    );
  }

  if (variant === 'normal') {
    return <Box>{children}</Box>;
  }

  if (variant === 'editor') {
    return <>{children}</>;
  }

  return <MainLayout wrapperSx={wrapperSx}> {children} </MainLayout>;
}

export default function Layout({
  variant = 'main',
  children,
  title,
  meta,
  backBtnHandler,
  ...others
}: LayoutProps) {
  const { userLoading } = useAuth();
  const { userPlanData } = useContext(CreditContext);
  const { userData } = useAppContext();
  const { setIsOpen } = useTour();
  const { welcomeToT2C } = useSettings();

  useEffect(() => {
    if (userData && userPlanData && !userLoading) {
      const userEmail = getTourUserEmail(userData);
      const hasCompletedTour = isTourCompletedForUser(welcomeToT2C, userEmail);
      const tourInitializedKey = getTourStorageKey('tourInitialized', userEmail);
      const tourInitialized = localStorage.getItem(tourInitializedKey);

      if (!tourInitialized && !hasCompletedTour) {
        setIsOpen(true);
        localStorage.removeItem(tourInitializedKey);
      }
    }
  }, [userData, userPlanData, welcomeToT2C, userLoading]);

  if (userLoading) {
    return <LoadingScreen />;
  }

  return (
    <Page title={title} meta={meta}>
      <LayoutSwitch variant={variant} backBtnHandler={backBtnHandler} {...others}>
        {children}
      </LayoutSwitch>
    </Page>
  );
}
